import { Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatRepsRange } from '@heraion/shared';

import { Badge } from '../primitives/Badge';
import { amrapStopSentence, amrapStopShort } from '../amrapStopWords';
import { amrapFloorShown, hasAmrapFloor } from './amrapIntent';
import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { makeFocusPanelInputsStyles } from './focusPanelInputsStyles';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';

// The REPS cell of FocusPanelInputs, with what the plan says about it on its
// label: the AMRAP floor and stop rule, and the rep range (#336 S3). Split out of
// FocusPanelInputs for the 500-line cap, at the seam between the reps cell and
// the weight cell; the parent keeps the row's error line and the focus
// reporter, which both cells share.
export function FocusPanelRepsField({
  set: s,
  invalid,
  onChange,
  fieldFocus,
  styles,
  theme,
}: {
  set: DraftSet;
  /** The reps text does not parse: the parent's error line says why. */
  invalid: boolean;
  onChange: (patch: Pick<DraftSet, 'reps'>) => void;
  fieldFocus: { onFocus: (id: string) => void; onBlur: (id: string) => void };
  styles: ReturnType<typeof makeFocusPanelInputsStyles>;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  // Tap a populated field: select the whole number so you type over it.
  const repsSel = useSelectAllOnFocus(s.reps);
  const range = s.repsRangePrescribed;
  const showAmrap = hasAmrapFloor(s);
  const floor = amrapFloorShown(s);
  return (
    <View style={styles.field}>
      {/* The AMRAP badge sits on the label and STAYS: a placeholder dies on
          the first keystroke, exactly when you still need to know the
          target is "at least N, then as many as possible" (LG-23). Display
          only, so <Badge>, never <Chip>. Shown for the plan's AMRAP and for
          the test the chip proposed (#539); the minimum only up to 12
          (amrapFloorShown). The rep range (#336 S3) sits in the same place
          for the same reason; the two never meet (Q6). */}
      <View style={styles.fieldLabelRow}>
        <Text style={styles.fieldLabel}>{t('mobile.logger.setReps')}</Text>
        {showAmrap ? (
          <Badge
            theme={theme}
            label={
              floor
                ? t('mobile.logger.amrapBadgeWithFloor', {
                    defaultValue: 'AMRAP ≥ {{reps}}',
                    reps: floor,
                  })
                : t('program.amrapBadge', { defaultValue: 'AMRAP' })
            }
            a11yLabel={
              floor
                ? t('mobile.logger.amrapPrescribedFloorA11y', {
                    defaultValue: 'AMRAP: at least {{reps}} reps, then as many as possible',
                    reps: floor,
                  })
                : t('mobile.logger.amrapChip', {
                    defaultValue: 'As many reps as possible',
                  })
            }
          />
        ) : range ? (
          <Badge
            theme={theme}
            label={formatRepsRange(range)}
            numeric
            a11yLabel={t('mobile.logger.repsRangeA11y', {
              defaultValue: 'Rep range {{min}} to {{max}}',
              min: range.min,
              max: range.max,
            })}
          />
        ) : null}
        {/* #355: where the plan's AMRAP stops, beside the AMRAP badge (D3). */}
        {showAmrap && s.amrapPrescribed && s.amrapStopPrescribed ? (
          <Badge
            theme={theme}
            label={amrapStopShort(t, s.amrapStopPrescribed)}
            a11yLabel={amrapStopSentence(t, s.amrapStopPrescribed)}
          />
        ) : null}
      </View>
      <TextInput
        ref={repsSel.ref}
        style={[styles.input, styles.inputNumeric, invalid && styles.inputInvalid]}
        keyboardType="number-pad"
        inputMode="numeric"
        // On an AMRAP the cell arrives EMPTY (LG-28) and the floor lives
        // here instead, as "≥ 8": a placeholder cannot be submitted by doing
        // nothing. Every other set keeps the bare dash.
        placeholder={
          floor
            ? t('mobile.logger.amrapFloorPlaceholder', {
                defaultValue: '≥ {{reps}}',
                reps: floor,
              })
            : '\u2014'
        }
        placeholderTextColor={theme.palette.ink3}
        value={s.reps}
        onChangeText={(reps) => onChange({ reps })}
        selectTextOnFocus
        onFocus={() => {
          repsSel.onFocus();
          fieldFocus.onFocus('reps');
        }}
        onBlur={() => fieldFocus.onBlur('reps')}
        maxLength={4}
      />
    </View>
  );
}
